import { Select } from './Select'
import { Badge } from './Badge'

export interface TabOption {
  value: string
  label: string
  testId?: string
  /** Optional count chip shown after the label (desktop tablist only). */
  badge?: string | number
}

export interface TabsProps {
  value: string
  onChange: (value: string) => void
  options: TabOption[]
  // When true (default) a mobile <Select> fallback renders below `sm` and the
  // desktop tablist is `hidden sm:flex`. Set false for bars that should stay a
  // horizontal tablist on every viewport (they manage their own overflow).
  mobileSelect?: boolean
  /** `bordered` (default, underline) or `boxed` (segmented pills) — use `boxed`
   *  for a SUB-level tab bar so it reads distinctly from a `bordered` parent. */
  variant?: 'bordered' | 'boxed'
  className?: string
  'aria-label'?: string
}

// EP-95 T15 — shared controlled tab bar. Pages keep their own URL/filter state
// and pass `value` + `onChange`; this component only renders the bar and the
// mobile Select fallback so tablist markup stops being hand-rolled per page.
export function Tabs({
  value,
  onChange,
  options,
  mobileSelect = true,
  variant = 'bordered',
  className = '',
  'aria-label': ariaLabel,
}: TabsProps) {
  return (
    <>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={`tabs ${variant === 'boxed' ? 'tabs-boxed' : 'tabs-bordered'} ${mobileSelect ? 'hidden sm:flex' : 'flex'} ${className}`.trim()}
      >
        {options.map((o) => {
          const active = o.value === value
          return (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={o.testId}
              onClick={() => onChange(o.value)}
              className={`tab whitespace-nowrap ${active ? 'tab-active' : ''}`}
            >
              {o.label}
              {o.badge != null && o.badge !== '' && (
                <Badge variant="count" size="sm" className="ml-2">{o.badge}</Badge>
              )}
            </button>
          )
        })}
      </div>

      {mobileSelect && (
        <Select
          className="sm:hidden"
          aria-label={ariaLabel}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={options.map((o) => ({ value: o.value, label: o.label }))}
        />
      )}
    </>
  )
}
